/**
 * @fileoverview O cadastro financeiro da Empresa como estado (03/11/2026): a
 * seção de Faturamento e a contratação usam o mesmo. Lê do servidor, guarda o
 * que está sendo digitado, preenche o endereço pelo CEP e salva. Falta de campo
 * obrigatório aponta todos de uma vez, sem chamar o servidor; o que o servidor
 * recusa (CPF, e-mail já usado) fica no campo dele. A resposta do CEP só vale se
 * nada mudou enquanto ela vinha: outro CEP, o endereço digitado à mão ou salvar.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { lookupZipCode } from "../../utils/zipCodeLookup";

const t = (key) => i18n.t(`billingProfile.${key}`);

export const onlyDigits = (value) => (value || "").replace(/\D/g, "");

const EMPTY = { personType: "company", name: "", document: "", email: "", phone: "", zipCode: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "" };
const toForm = (profile) => (profile ? Object.keys(EMPTY).reduce((form, key) => ({ ...form, [key]: profile[key] || EMPTY[key] }), {}) : EMPTY);

// Na ordem da tela: o primeiro que falta recebe o foco. O complemento é opcional.
const REQUIRED = ["name", "document", "email", "phone", "zipCode", "street", "number", "neighborhood", "city", "state"];
const ZIP_FILLS = ["street", "neighborhood", "city", "state"];

const FIELD_OF_ERROR = {
  ERR_BILLING_NAME: "name",
  ERR_BILLING_CPF: "document",
  ERR_BILLING_CNPJ: "document",
  ERR_BILLING_EMAIL: "email",
  ERR_BILLING_EMAIL_IN_USE: "email",
  ERR_BILLING_PHONE: "phone",
  ERR_BILLING_ZIP: "zipCode",
  ERR_BILLING_ADDRESS: "street",
  ERR_BILLING_STATE: "state",
};

export const useBillingProfile = ({ active = true, prefill = null } = {}) => {
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadNonce, setLoadNonce] = useState(0);
  // O campo que deve receber o foco (o primeiro com erro, ou o número depois do CEP).
  const [focusField, setFocusField] = useState(null);
  const lastZip = useRef("");
  const zipRequest = useRef(0);
  const prefillRef = useRef(prefill);

  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    setLoading(true);
    setLoadFailed(false);
    api
      .get("/billing/profile")
      .then(({ data }) => {
        if (!alive) return;
        setSaved(data.profile ? toForm(data.profile) : null);
        setForm(data.profile ? toForm(data.profile) : { ...EMPTY, ...prefillRef.current });
        lastZip.current = data.profile?.zipCode || "";
      })
      .catch((err) => {
        if (alive) setLoadFailed(true);
        toastError(err);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [active, loadNonce]);

  const reload = useCallback(() => setLoadNonce((nonce) => nonce + 1), []);

  const base = saved || EMPTY;
  const dirty = Object.keys(EMPTY).some((key) => (form[key] || "") !== (base[key] || ""));

  // Uma busca do CEP em andamento deixa de valer.
  const forgetZipRequest = useCallback(() => {
    zipRequest.current += 1;
    setZipLoading(false);
  }, []);

  const setField = useCallback(
    (key, value) => {
      if (ZIP_FILLS.includes(key)) forgetZipRequest();
      setForm((current) => ({ ...current, [key]: value }));
      setErrors((current) => (current[key] ? { ...current, [key]: "" } : current));
    },
    [forgetZipRequest]
  );

  const setZipCode = useCallback(
    async (value) => {
      const digits = onlyDigits(value).slice(0, 8);
      setForm((current) => ({ ...current, zipCode: digits }));
      setErrors((current) => (current.zipCode ? { ...current, zipCode: "" } : current));
      if (digits === lastZip.current) return;
      forgetZipRequest();
      lastZip.current = digits.length === 8 ? digits : "";
      if (digits.length !== 8) return;
      const request = zipRequest.current;
      setZipLoading(true);
      const address = await lookupZipCode(digits);
      if (request !== zipRequest.current) return;
      setZipLoading(false);
      if (!address) return;
      setForm((current) => ({ ...current, ...address }));
      setErrors((current) => ({ ...current, street: "", neighborhood: "", city: "", state: "" }));
      // Só leva o foco de quem ainda está no CEP (a pessoa pode ter seguido adiante).
      if (document.activeElement?.id?.endsWith("-zipCode")) setFocusField(address.street ? "number" : "street");
    },
    [forgetZipRequest]
  );

  const discard = useCallback(() => {
    forgetZipRequest();
    setForm(saved || EMPTY);
    setErrors({});
    lastZip.current = saved?.zipCode || "";
  }, [saved, forgetZipRequest]);

  /**
   * Salva e diz o que houve: `ok`; `handled` quando a tela já mostra o motivo (o que
   * falta, o erro de um campo); senão, `err` e `code` para quem chamou mostrar.
   */
  const save = useCallback(async () => {
    const missing = REQUIRED.filter((key) => !String(form[key] || "").trim());
    if (missing.length) {
      setErrors(missing.reduce((all, key) => ({ ...all, [key]: t("required") }), {}));
      setFocusField(missing[0]);
      return { ok: false, handled: true };
    }
    forgetZipRequest();
    setSaving(true);
    setErrors({});
    try {
      const { data } = await api.put("/billing/profile", form);
      setSaved(toForm(data.profile));
      setForm(toForm(data.profile));
      lastZip.current = data.profile?.zipCode || "";
      return { ok: true };
    } catch (err) {
      const code = err?.response?.data?.error;
      const field = FIELD_OF_ERROR[code];
      if (field && i18n.exists(`backendErrors.${code}`)) {
        setErrors({ [field]: i18n.t(`backendErrors.${code}`) });
        setFocusField(field);
        return { ok: false, handled: true };
      }
      return { ok: false, handled: false, code, err };
    } finally {
      setSaving(false);
    }
  }, [form, forgetZipRequest]);

  const clearFocusField = useCallback(() => setFocusField(null), []);

  return { saved, form, loading, loadFailed, reload, saving, zipLoading, errors, dirty, focusField, clearFocusField, setField, setZipCode, discard, save };
};
